<template>
    <Splide :has-track="false" class="splide__gallery h-full -mx-4 lg:mx-0" :options="options" @splide:move="loadAllSlides">
        <SplideTrack class="h-full">
            <SplideSlide
                v-for="(item, index) in images" :key="item.id"
                class="overflow-hidden"
            >
                <picture
                    class="w-full select-none"
                    width="480"
                    height="480"
                    v-if="item.src"
                >
                    <source
                    :srcset="`
                        ${item.src}?tr=w-1920,
                        ${item.src}?tr=w-3840 2x
                    `"
                    media="(min-width:1480px)"
                    />
                    <source
                    :srcset="`
                        ${item.src}?tr=w-1480,
                        ${item.src}?tr=w-2960 2x
                    `"
                    media="(min-width:1000px)"
                    />
                    <!-- Whole photos, cropped once here around their focus point. The box is 290px tall on phones
                         and most of the screen's height on tablets, so the photo shows wider than the screen -->
                    <img class="lg:h-[790px] w-full h-[290px] sm:h-full object-cover sm:aspect-auto"
                        :loading="index === 0 ? 'eager' : 'lazy'" :src="`${item.src}?tr=w-700`"
                        :srcset="`${item.src}?tr=w-700 700w, ${item.src}?tr=w-1000 1000w, ${item.src}?tr=w-1400 1400w, ${item.src}?tr=w-2200 2200w`"
                        :style="{ objectPosition: item.focus }"
                        sizes="(min-width: 640px) 160vw, 115vw" :alt="`${item.alt} image`">
                </picture>
            </SplideSlide>
        </SplideTrack>

        <div class="splide__arrows flex justify-between absolute top-1/2 left-0 w-full -mt-8 px-8">
            <button class="splide__arrow--prev w-11 h-11 flex justify-center items-center rounded-full transition-all duration-200"
                    :class="pageStyle ? `bg-${pageStyle}-primary hover:bg-${pageStyle}-primary-tone` : 'bg-primary hover:bg-primary-tone'">
                <svg class="relative -left-px fill-dark" width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.22748 8.80219L2.37798 15.6517C1.94989 16.1155 1.20072 16.1155 0.772631 15.6517C0.308863 15.2236 0.308863 14.4744 0.772631 14.0463L6.80162 7.98167L0.772631 1.95269C0.308863 1.5246 0.308863 0.775431 0.772631 0.347338C1.20072 -0.11643 1.94989 -0.11643 2.37798 0.347338L9.22748 7.19684C9.69125 7.62493 9.69125 8.37409 9.22748 8.80219Z" />
                </svg>
            </button>

            <button class="splide__arrow--next w-11 h-11 flex justify-center items-center rounded-full transition-all duration-200"
                    :class="pageStyle ? `bg-${pageStyle}-primary hover:bg-${pageStyle}-primary-tone` : 'bg-primary hover:bg-primary-tone'">
                <svg class="relative -right-px fill-dark" width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.22748 8.80219L2.37798 15.6517C1.94989 16.1155 1.20072 16.1155 0.772631 15.6517C0.308863 15.2236 0.308863 14.4744 0.772631 14.0463L6.80162 7.98167L0.772631 1.95269C0.308863 1.5246 0.308863 0.775431 0.772631 0.347338C1.20072 -0.11643 1.94989 -0.11643 2.37798 0.347338L9.22748 7.19684C9.69125 7.62493 9.69125 8.37409 9.22748 8.80219Z" />
                </svg>
            </button>
        </div>
    </Splide>
</template>

<script>
import {defineComponent} from 'vue'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import ResponsiveMixin from "./../mixins/responsive"

export default defineComponent({
    components: {
        Splide,
        SplideSlide,
        SplideTrack,
    },
    mixins: [ResponsiveMixin],
    props: {
        pageStyle: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            options: {
                type: 'loop',
                perPage: 1,
                arrows: true,
                pagination: false,
                breakpoints: {
                    768: {
                        arrows: false,
                        pagination: true,
                    },
                },
            },
        }
    },
    computed: {
        images() {
            const rawImages = window.capes?.experience_gallery
            const rawHero = window.capes?.hero_image

            const gallery = Array.isArray(rawImages) ? rawImages.slice() : []

            const heroId = rawHero?.ID ?? rawHero?.id
            const heroUrl = rawHero?.url
            const hasHero = !!(rawHero && (heroId || heroUrl || rawHero?.sizes))

            const alreadyHasHero = hasHero
                ? gallery.some((img) => {
                    const imgId = img?.ID ?? img?.id
                    const imgUrl = img?.url
                    return (heroId && imgId && imgId === heroId) || (heroUrl && imgUrl && imgUrl === heroUrl)
                })
                : false

            const combined = hasHero && !alreadyHasHero ? [rawHero, ...gallery] : gallery

            return combined.filter(Boolean).map((image) => {
                return {
                    id: image.ID ?? image.id ?? image.url,
                    // The full-size file: the image Worker resizes it to each ?tr= size, and the
                    // 2048px copy was too small for the 2x desktop sizes
                    src: image.url,
                    thumb: `${image.sizes?.medium_large ?? image.url}`,
                    alt: image.alt || (image.title + ' image'),
                    focus: image.focus || '50% 50%',
                }
            })
        },
    },
    methods: {
        // Only the first slide loads with the page. Once someone moves the carousel, load the rest
        // so the next slides are ready. Splide's loop clones aren't Vue's, so this goes via the DOM
        loadAllSlides(splide) {
            splide.root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
                img.loading = 'eager'
            })
        },
    },
    created() {
        // console.log(this.images)
    },
})
</script>